import React from "react";
import { observer } from "mobx-react-lite";

import pl from "shared/messages/pl";
import { BasicTable, RegularText, TextTableHeader } from "shared/components";
import { Profile } from "view360Card/models";
import { MobileViewCard } from "../mobileViewCard/MobileViewCard";
import { constants } from "shared/constants";
import { MobileViewCardDetailsItem } from "../mobileViewCardDetailsItem/MobileViewCardDetailsItem";
import styles from "./profilesTable.module.scss";

export interface ProfilesTableProps {
    profiles: Profile[];
}

export const ProfilesTable: React.FC<ProfilesTableProps> = observer((props) => {
    const headerItems = [
        {
            content: (
                <TextTableHeader
                    content={`${pl.view360Card.profilesTable.number} ${constants.inlineSeparator} ${pl.view360Card.profilesTable.tenant}`}
                />
            ),
            key: "number-tenant",
            className: styles.columnNumberTenant,
        },
        {
            content: <TextTableHeader content={pl.view360Card.profilesTable.status} />,
            key: "status",
        },
        {
            content: <TextTableHeader content={pl.view360Card.profilesTable.channel} />,
            key: "channel",
        },
        {
            content: <TextTableHeader content={pl.view360Card.profilesTable.distributionGroup} />,
            key: "distributionGroup",
        },
        {
            content: <TextTableHeader content={pl.view360Card.profilesTable.proxy} />,
            key: "proxy",
        },
        {
            content: (
                <TextTableHeader
                    content={`${pl.view360Card.profilesTable.pd} ${constants.inlineSeparator} ${pl.view360Card.profilesTable.rms}`}
                />
            ),
            key: "pd-rms",
        },
    ];

    const getRows = () => {
        return props.profiles.map((profile) => {
            return {
                key: profile.id,
                items: [
                    {
                        className: styles.columnNumberTenant,
                        key: `${profile.id}-number-tenant`,
                        content: (
                            <RegularText
                                content={`${profile.number ?? constants.noValue} ${constants.inlineSeparator} ${
                                    profile.tenantCode ?? constants.noValue
                                }`}
                            />
                        ),
                    },
                    {
                        key: `${profile.id}-status`,
                        content: <RegularText content={profile.status?.description ?? constants.noValue} />,
                    },
                    {
                        key: `${profile.id}-channel`,
                        content: <RegularText content={profile.channel?.description ?? constants.noValue} />,
                    },
                    {
                        key: `${profile.id}-distributionGroup`,
                        content: <RegularText content={profile.distributionGroup?.description ?? constants.noValue} />,
                    },
                    {
                        key: `${profile.id}-proxy`,
                        content: <RegularText content={profile.proxy?.description ?? constants.noValue} />,
                    },
                    {
                        key: `${profile.id}-pd-rms`,
                        content: (
                            <RegularText
                                content={`${profile.branch?.name ?? constants.noValue} ${constants.inlineSeparator} ${
                                    profile.rms?.fullName ?? constants.noValue
                                }`}
                            />
                        ),
                    },
                ],
            };
        });
    };

    const getMobileHeader = (profile: Profile) => {
        return `${profile.number ?? constants.noValue} ${constants.inlineSeparator} ${
            profile.tenantCode ?? constants.noValue
        }`;
    };

    const getMobilViewDetailItem = (profile: Profile) => {
        return [
            <MobileViewCardDetailsItem
                label={pl.view360Card.profilesTable.status}
                content={profile.status?.description ?? constants.noValue}
            />,
            <MobileViewCardDetailsItem
                label={pl.view360Card.profilesTable.channel}
                content={profile.channel?.description ?? constants.noValue}
            />,
            <MobileViewCardDetailsItem
                label={pl.view360Card.profilesTable.distributionGroup}
                content={profile.distributionGroup?.description ?? constants.noValue}
            />,
            <MobileViewCardDetailsItem
                label={pl.view360Card.profilesTable.proxy}
                content={profile.proxy?.description ?? constants.noValue}
            />,
            <MobileViewCardDetailsItem
                label={`${pl.view360Card.profilesTable.pd} ${constants.inlineSeparator} ${pl.view360Card.profilesTable.rms}`}
                content={`${profile.branch?.name ?? constants.noValue} ${constants.inlineSeparator} ${
                    profile.rms?.fullName ?? constants.noValue
                }`}
            />,
        ];
    };

    const getMobileViewRows = () => {
        if (!props.profiles) {
            return null;
        }

        return props.profiles.map((profile) => {
            return <MobileViewCard key={profile.id} header={getMobileHeader(profile)} detailsItems={getMobilViewDetailItem(profile)} />;
        });
    };

    return (
        <BasicTable
            rows={getRows()}
            headerItems={headerItems}
            mobileViewRows={getMobileViewRows()}
            maxDefaultSize={constants.tableMaxDefaultSize}
        />
    );
});
